import React, {useContext, useEffect, useState} from 'react';
import {StyleSheet, Text, View, Image, Pressable, ScrollView} from 'react-native';
import { GlobalStateContext } from '../../components/general/GlobalStateProvider';
import AccountCard from './components/AccountCard';
import ProfileCard from './components/ProfileCard';

const AccountPage = () => {
    const {padding: padding, user: user, isLoadingUser, setShowLoginModal} = useContext(GlobalStateContext);
    
    useEffect(() => {
        // Only show login modal if loading is complete and user is not found
        if (!isLoadingUser && !user) {
            setShowLoginModal(true);
            return;
        }
    }, [user, isLoadingUser, setShowLoginModal]);

    // Get dynamic text based on account type
    const getAccountInfoText = () => {
        switch (user?.accountType) {
            case 'host':
                return 'Manage coaches, personal information and account preferences';
            case 'coach':
                return 'Manage personal information and account preferences';
            case 'family':
            default:
                return 'Manage family members, personal information and account preferences';
        }
    };

    const getFavoritesText = () => {
        switch (user?.accountType) {
            case 'host':
                return 'View saved camps and sessions for your programs';
            case 'coach':
                return 'View saved camps and sessions you work with';
            case 'family':
            default:
                return 'View saved camps and sessions for your family';
        }
    };

    const getAccountInfoTitle = () => {
        switch (user?.accountType) {
            case 'host':
                return 'Host Info';
            case 'coach':
                return 'Account Info';
            case 'family':
            default:
                return 'Family Info';
        }
    };

    const getOwnerLabel = () => {
        switch (user?.accountType) {
            case 'host':
                return 'Camp Host';
            case 'coach':
                return 'Coach';
            case 'family':
            default:
                return 'Parent Account';
        }
    };

    const getMemberLabel = () => {
        return user?.accountType === 'host' ? 'Coach' : 'Child';
    };

    const getProfileTitle = () => {
        switch (user?.accountType) {
            case 'host':
                return 'Host Profile';
            case 'coach':
                return 'Account Profile';
            case 'family':
            default:
                return 'Family Profile';
        }
    };

    return (
        <ScrollView 
            style={styles.container}
            contentContainerStyle={[styles.contentContainer, { paddingHorizontal: padding }]}
        >
            <View style={styles.titleContainer}>
                <Text style={styles.titleText}>{getProfileTitle()}</Text>
            </View>

            <View style={styles.profilesContainer}>
                {user && (
                    <ProfileCard
                        profileImage={user.profileImage}
                        firstName={user.firstName || ''}
                        lastName={user.lastName || ''}
                        email={user.email}
                        userType={getOwnerLabel()}
                        accountType={user.accountType}
                    />
                )}
                
                {user?.children && user.children.length > 0 && user?.accountType !== 'coach' && user.children.map((child) => (
                    <ProfileCard
                        key={child.childId}
                        profileImage={child.profileImage}
                        firstName={child.firstName}
                        lastName={child.lastName}
                        userType={getMemberLabel()}
                        age={child.age}
                    />
                ))}
            </View>

            <View style={styles.cardContainer}>
                <AccountCard 
                    iconName='card-account-details-outline' 
                    title={getAccountInfoTitle()} 
                    text={getAccountInfoText()}
                />
                <AccountCard 
                    iconName='credit-card-outline' 
                    title='Payments' 
                    text='Add payment methods for camp registrations and purchases'
                />
                <AccountCard 
                    iconName='heart-multiple-outline' 
                    title='Favorites' 
                    text={getFavoritesText()}
                />
            </View>

        </ScrollView>
    );
}

const styles = StyleSheet.create({
    titleText: {
        fontSize: 35,
        fontWeight: 'bold',
        marginBottom: 40,
    },
    titleContainer: {
        marginBottom: 30,
    },
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    contentContainer: {
        minHeight: '100%',
        paddingBottom: 50,
        paddingTop: 24,
    },
    profilesContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 20,
        marginBottom: 30,
    },
    cardContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        marginTop: 20,
        marginBottom: 30,
    },
});

export default AccountPage;